import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Plus, CheckCircle2, Circle, Clock, AlertTriangle, ListTodo } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatRel } from "./constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function bucket(task) {
  if (task.completed) return "completed";
  if (!task.due_date) return "no-due";
  const due = new Date(task.due_date).getTime();
  const now = Date.now();
  const diffMs = due - now;
  const day = 24 * 60 * 60 * 1000;
  if (diffMs < 0) return "overdue";
  if (diffMs < day) return "today";
  if (diffMs < 7 * day) return "this-week";
  return "later";
}

const BUCKET_ORDER = ["overdue", "today", "this-week", "later", "no-due", "completed"];
const BUCKET_LABEL = {
  overdue: { title: "Overdue", icon: AlertTriangle, tone: "text-rose-700" },
  today: { title: "Due today", icon: Clock, tone: "text-amber-700" },
  "this-week": { title: "This week", icon: Clock, tone: "text-cyan-700" },
  later: { title: "Later", icon: Clock, tone: "text-slate-600" },
  "no-due": { title: "No due date", icon: ListTodo, tone: "text-slate-500" },
  completed: { title: "Completed", icon: CheckCircle2, tone: "text-emerald-700" },
};

export default function AdminCrmTasksPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/crm/tasks`);
      setItems(data.items);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tasks");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const out = Object.fromEntries(BUCKET_ORDER.map((k) => [k, []]));
    for (const t of items) out[bucket(t)].push(t);
    return out;
  }, [items]);

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/crm/tasks`, {
        title: title.trim(),
        due_date: due ? new Date(due).toISOString() : null,
        priority: "normal",
      });
      setTitle(""); setDue(""); setAdding(false);
      toast.success("Task added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (task) => {
    try {
      await axios.put(`${API}/admin/crm/tasks/${task.id}`, { completed: !task.completed });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="crm-tasks-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">CRM · Tasks</h1>
          <p className="text-sm text-slate-500 mt-0.5">{items.length} task{items.length === 1 ? "" : "s"}</p>
        </div>
        <Button size="sm" onClick={() => setAdding((v) => !v)} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="crm-tasks-new-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New task
        </Button>
      </div>

      {adding && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 grid sm:grid-cols-3 gap-3" data-testid="crm-tasks-new-form">
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title…" className="text-sm rounded-md border border-slate-300 px-3 py-2 sm:col-span-2" data-testid="crm-tasks-new-title" />
          <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} className="text-sm rounded-md border border-slate-300 px-3 py-2" data-testid="crm-tasks-new-due" />
          <div className="sm:col-span-3 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={busy || !title.trim()} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="crm-tasks-new-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Add
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500">
          <ListTodo className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm">No tasks yet. Create one above or add tasks from a contact's detail page.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {BUCKET_ORDER.map((b) => {
            const list = grouped[b];
            if (!list.length) return null;
            const meta = BUCKET_LABEL[b];
            const Icon = meta.icon;
            return (
              <div key={b} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`crm-tasks-bucket-${b}`}>
                <h2 className={`text-sm font-semibold mb-3 inline-flex items-center gap-1.5 ${meta.tone}`}>
                  <Icon className="w-4 h-4" /> {meta.title} <span className="text-slate-400">({list.length})</span>
                </h2>
                <ul className="divide-y divide-slate-100">
                  {list.map((t) => (
                    <li key={t.id} className="flex items-start gap-3 py-2.5" data-testid={`crm-task-row-${t.id}`}>
                      <button onClick={() => toggle(t)} className="mt-0.5" data-testid={`crm-task-toggle-${t.id}`}>
                        {t.completed ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-slate-400 hover:text-cyan-700" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm ${t.completed ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                          {t.due_date && <span>Due {new Date(t.due_date).toLocaleString()}</span>}
                          {t.contact && (
                            <Link to={`/admin/crm/contacts/${t.contact_id}`} className="text-cyan-700 hover:underline">
                              {t.contact.name}{t.contact.organization ? ` · ${t.contact.organization}` : ""}
                            </Link>
                          )}
                          <span className="text-slate-400">created {formatRel(t.created_at)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
